'use client'

import { useRef } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import { Check, Minus } from 'lucide-react'

import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@mdxui/primitives'

import { cn } from '../../../lib/cn'
import { Markdown } from '../../../lib/markdown'
import {
  sectionVariants,
  headerVariants,
  reducedMotionVariants,
} from '../../../lib/motion'

interface PricingAgentsAttribute {
  /** Unique key to match with agent values */
  key: string
  /** Display label for the row */
  label: string
}

interface PricingAgentsAgent {
  /** Agent name */
  name: string
  /** Agent avatar */
  image?: {
    src: string
    alt: string
  }
  /** CTA button text */
  buttonText?: string
  /** CTA button URL */
  buttonUrl?: string
  /** Highlight this agent column */
  highlighted?: boolean
  /** Attribute values - key matches attributes[].key */
  values: Record<string, string | boolean>
}

const defaultAttributes: PricingAgentsAttribute[] = [
  { key: 'frontend', label: 'Frontend Development' },
  { key: 'backend', label: 'Backend Development' },
  { key: 'security', label: 'Security Audits' },
  { key: 'price', label: 'Starting Price' },
]

const defaultAgents: PricingAgentsAgent[] = [
  {
    name: 'Tom',
    image: { src: '/avatars/Tom.png', alt: 'Tom' },
    buttonText: 'Hire Tom',
    buttonUrl: '/agents/tom',
    values: {
      frontend: true,
      backend: true,
      security: false,
      price: '$10,000',
    },
  },
  {
    name: 'Priya',
    image: { src: '/avatars/Priya.png', alt: 'Priya' },
    buttonText: 'Hire Priya',
    buttonUrl: '/agents/priya',
    highlighted: true,
    values: {
      frontend: true,
      backend: false,
      security: false,
      price: '$2,000',
    },
  },
  {
    name: 'Sam',
    image: { src: '/avatars/Sam.png', alt: 'Sam' },
    buttonText: 'Hire Sam',
    buttonUrl: '/agents/sam',
    values: {
      frontend: false,
      backend: true,
      security: true,
      price: '$2,500',
    },
  },
]

/**
 * Props for PricingAgents component
 *
 * @remarks
 * **IMPORTANT CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - Supports 2-4 agents for comparison
 * - Attributes can be boolean (check/dash) or text values
 * - Use highlighted prop to emphasize recommended agent
 * - Use teamMode to show unified "Hire the Team" CTA instead of individual buttons
 */
export interface PricingAgentsProps {
  /** Section heading */
  heading?: string
  /** Section description */
  description?: string
  /** Comparison attributes (rows) */
  attributes?: PricingAgentsAttribute[]
  /** Agents to compare (columns) */
  agents?: PricingAgentsAgent[]
  /** Team mode - shows capability coverage with single "Hire the Team" CTA */
  teamMode?: boolean
  /** Team CTA button text (team mode only) */
  teamButtonText?: string
  /** Team CTA button URL (team mode only) */
  teamButtonUrl?: string
  /** Team pricing display (team mode only) */
  teamPrice?: string
  /** Additional CSS classes */
  className?: string
}

/**
 * PricingAgents - Agent comparison table
 *
 * **Compare Mode (default):**
 * ```
 * ┌─────────────┬──────────┬──────────┬──────────┐
 * │             │  (○)Tom  │ (○)Priya │  (○)Sam  │
 * ├─────────────┼──────────┼──────────┼──────────┤
 * │ Frontend    │    ✓     │    ✓     │    —     │
 * │ Backend     │    ✓     │    —     │    ✓     │
 * ├─────────────┼──────────┼──────────┼──────────┤
 * │             │  [Hire]  │  [Hire]  │  [Hire]  │
 * └─────────────┴──────────┴──────────┴──────────┘
 * ```
 *
 * **Team Mode (teamMode=true):**
 * ```
 * ┌─────────────┬──────────┬──────────┬──────────┐
 * │             │  (○)Tom  │ (○)Priya │  (○)Sam  │
 * ├─────────────┼──────────┼──────────┼──────────┤
 * │ Frontend    │    ✓     │    ✓     │    —     │
 * │ Backend     │    ✓     │    —     │    ✓     │
 * │ Security    │    —     │    —     │    ✓     │
 * ├─────────────┴──────────┴──────────┴──────────┤
 * │  Full Stack Coverage · $15k/mo               │
 * │           [ Hire the Team ]                  │
 * └──────────────────────────────────────────────┘
 * ```
 *
 * **Best Use Cases:**
 * - Compare mode: Help users choose between agents
 * - Team mode: Show complete dev stack coverage, sell the whole team
 */
const PricingAgents = ({
  heading = 'Compare Our Experts',
  description = 'Find the right agent for your project.',
  attributes = defaultAttributes,
  agents = defaultAgents,
  teamMode = false,
  teamButtonText = 'Hire the Team',
  teamButtonUrl = '/contact',
  teamPrice,
  className,
}: PricingAgentsProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion ? reducedMotionVariants : headerVariants

  // Dynamic max-width based on number of agents
  const maxWidthClass =
    agents.length <= 2 ? 'max-w-2xl' : agents.length === 3 ? 'max-w-3xl' : 'max-w-4xl'

  const renderValue = (value: string | boolean | undefined) => {
    if (value === true) {
      return <Check className="mx-auto size-5 text-primary" />
    }
    if (value === false || value === undefined) {
      return <Minus className="mx-auto size-5 text-muted-foreground/50" />
    }
    return <span>{value}</span>
  }

  return (
    <motion.section
      ref={sectionRef}
      className={cn('py-16 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container mx-auto">
        {(heading || description) && (
          <motion.div className="mx-auto mb-10 max-w-2xl text-center lg:mb-12" variants={headerAnimation}>
            {heading && ( 
              <h2 className="text-pretty text-3xl font-semibold lg:text-4xl">{heading}</h2>
            )}
            {description && (
              <div className="mt-4"><Markdown size="base">{description}</Markdown></div>
            )}
          </motion.div>
        )}

        <motion.div
          className={cn('mx-auto overflow-hidden border border-border', maxWidthClass)}
          style={{ borderRadius: 'var(--radius)' }}
          variants={headerAnimation}
        >
          <div className="overflow-x-auto">
            <Table>
              {/* Header with avatars and names */}
              <TableHeader>
                <TableRow className="bg-muted">
                  <TableHead className="sticky left-0 min-w-[120px] bg-muted md:min-w-[160px]" />
                  {agents.map((agent) => (
                    <TableHead
                      key={agent.name}
                      className={cn(
                        'min-w-[100px] px-3 py-4 text-center md:min-w-[120px] md:px-4',
                        agent.highlighted && 'bg-muted'
                      )}
                    >
                      <div className="flex flex-col items-center gap-1.5 md:gap-2">
                        {agent.image && (
                          <img
                            src={agent.image.src}
                            alt={agent.image.alt}
                            className="size-8 rounded-full object-cover md:size-10"
                          />
                        )}
                        <span className="text-xs font-medium md:text-sm">{agent.name}</span>
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>

              <TableBody>
                {/* Attribute rows */}
                {attributes.map((attribute) => (
                  <TableRow key={attribute.key}>
                    <TableCell className="sticky left-0 min-w-[120px] bg-background px-3 py-2.5 text-sm font-medium md:min-w-[160px] md:px-4 md:py-3">
                      {attribute.label}
                    </TableCell>
                    {agents.map((agent, index) => (
                      <TableCell
                        key={index.toString()}
                        className={cn(
                          'min-w-[100px] px-3 py-2.5 text-center text-muted-foreground md:min-w-[120px] md:px-4 md:py-3',
                          agent.highlighted && 'bg-muted/50'
                        )}
                      >
                        {renderValue(agent.values[attribute.key])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

                {/* CTA row - only for compare mode */}
                {!teamMode && (
                  <TableRow>
                    <TableCell className="sticky left-0 min-w-[120px] bg-background px-3 py-3 md:min-w-[160px] md:px-4 md:py-4" />
                    {agents.map((agent, index) => (
                      <TableCell
                        key={index.toString()}
                        className={cn(
                          'min-w-[100px] px-3 py-3 text-center md:min-w-[120px] md:px-4 md:py-4',
                          agent.highlighted && 'bg-muted/50'
                        )}
                      >
                        <Button
                          variant={agent.highlighted ? 'default' : 'outline'}
                          size="sm"
                          asChild
                        >
                          <a href={agent.buttonUrl || '#'}>
                            {agent.buttonText || `Hire ${agent.name}`}
                          </a>
                        </Button>
                      </TableCell>
                    ))}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </motion.div>

        {/* Team mode CTA - outside table so always visible */}
        {teamMode && (
          <motion.div
            className={cn('mx-auto mt-8 text-center', maxWidthClass)}
            variants={headerAnimation}
          >
            <div className="flex flex-col items-center gap-3">
              {teamPrice && (
                <p className="text-sm text-muted-foreground">
                  Full Stack Coverage · <span className="font-semibold text-foreground">{teamPrice}</span>
                </p>
              )}
              <Button size="lg" asChild>
                <a href={teamButtonUrl}>{teamButtonText}</a>
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </motion.section>
  )
}

export { PricingAgents }
export type { PricingAgentsAttribute, PricingAgentsAgent }
